import { DateRange } from '@blueprintjs/datetime';
import { DateRangeWrapper, DateRangeHiddenInput } from 'app/components/date-range-field/DateRangeField.styles';
import { DateRangeSelector2, DateRangeText, DropdownButton } from 'app/components/date-range-selector';
import { FilterLabel } from 'app/views/components/filter';
import { useI18n } from 'core/hooks/useI18n';
import { DateTime } from 'luxon';
import { ComponentProps, ReactNode, useMemo } from 'react';

type DateRangeFieldState = {
  savedDateRange: DateRange;
  savedDateTimeRange: [DateTime, DateTime] | undefined;
  isOpen: boolean;
  selectedDateRange: DateRange;
  onSave: (selectedDates: DateRange) => void;
  onChange: (dateRange: DateRange) => void;
  onClear: () => void;
  setIsOpen: (isOpen: boolean) => void;
};

type DateRangeSelectorProps = Omit<
  ComponentProps<typeof DateRangeSelector2>,
  'value' | 'onSave' | 'onClear' | 'popoverProps' | 'timePrecision' | 'onChange'
> & {
  popoverProps?: Omit<NonNullable<ComponentProps<typeof DateRangeSelector2>['popoverProps']>, 'isOpen'>;
};

type DateRangeFieldProps = {
  state: DateRangeFieldState;
  id: string;
  label: ReactNode;
  dateRangeSelectorProps?: DateRangeSelectorProps;
  labelProps?: Omit<ComponentProps<typeof FilterLabel>, 'htmlFor'>;
  buttonProps?: Omit<ComponentProps<typeof DropdownButton>['buttonProps'], 'title'>;
};

export const DateRangeField = ({
  state,
  id,
  label,
  dateRangeSelectorProps,
  buttonProps,
  labelProps,
}: DateRangeFieldProps) => {
  const { isOpen, onChange, onClear, onSave, savedDateRange, savedDateTimeRange, setIsOpen } = state;
  const { l10n } = useI18n('app/components', 'date');
  const text = useMemo(() => {
    const [from, to] = savedDateRange;
    const fromLocale = from ? DateTime.fromJSDate(from).toLocaleString(DateTime.DATETIME_MED) : '-';
    const toLocale = to ? DateTime.fromJSDate(to).toLocaleString(DateTime.DATETIME_MED) : '-';
    return l10n('selectionDescription', { from: fromLocale, to: toLocale });
  }, [l10n, savedDateRange]);
  const dateRangeMillis = useMemo(() => {
    if (!savedDateTimeRange) return;
    const [from, to] = savedDateTimeRange;
    return [from.valueOf(), to.valueOf()] as [number, number];
  }, [savedDateTimeRange]);
  const { popoverProps, ...restDateRangeSelectorProps } = dateRangeSelectorProps ?? {};
  const { onClose: onPopoverClose, ...restPopoverProps } = popoverProps ?? {};
  return (
    <DateRangeWrapper>
      <FilterLabel htmlFor={id} {...labelProps}>
        {label}
      </FilterLabel>
      <DateRangeHiddenInput id={id} readOnly value={text} onFocus={() => setIsOpen(true)} tabIndex={-1} />
      <DateRangeSelector2
        value={savedDateRange}
        onSave={onSave}
        onClear={onClear}
        popoverProps={{
          isOpen,
          onClose: event => {
            setIsOpen(false);
            onPopoverClose?.(event);
          },
          ...restPopoverProps,
        }}
        timePrecision="minute"
        onChange={onChange}
        {...restDateRangeSelectorProps}
      >
        <DropdownButton
          onClick={() => setIsOpen(!isOpen)}
          isActive={isOpen}
          hasValue={!!savedDateTimeRange}
          buttonProps={{ title: text, ...buttonProps }}
        >
          <DateRangeText dateRange={dateRangeMillis} />
        </DropdownButton>
      </DateRangeSelector2>
    </DateRangeWrapper>
  );
};
